import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { authSlice } from "@/store/auth";

import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppButton from "@/components/AppButton";
import AppDropdown from "@/components/AppDropdown";
import AppText from "@/components/AppText";
import AppIcon from "@/components/AppIcon";
import { usersUpdateUserNotAdmin } from "@/store/users";

interface AfterHoursModalProps {
  onClose: () => void;
}

type DayKey = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

const dayLabels: Record<DayKey, string> = {
  sun: "S",
  mon: "M",
  tue: "T",
  wed: "W",
  thu: "T",
  fri: "F",
  sat: "S",
};

const defaultHours = { start: "09:00", end: "17:00", enabled: true };

const timezones = [
  { label: "Eastern", value: "America/New_York" },
  { label: "Central", value: "America/Chicago" },
  { label: "Mountain", value: "America/Denver" },
  { label: "Pacific", value: "America/Los_Angeles" },
  { label: "Outside US", value: "outside_us" },
];

// Generate 15-minute interval options with 12-hour labels but 24-hour values
const generateTimeOptions = () => {
  const options: { label: string; value: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const value = `${h.toString().padStart(2, "0")}:${m
        .toString()
        .padStart(2, "0")}`;
      const hour12 = ((h + 11) % 12) + 1; // converts 0-23 to 1-12
      const suffix = h >= 12 ? "PM" : "AM";
      const label = `${hour12}:${m.toString().padStart(2, "0")} ${suffix}`;
      options.push({ label, value });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const AfterHoursModal: React.FC<AfterHoursModalProps> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const { me } = useAppSelector((s) => s.auth);

  const [hours, setHours] = useState<Record<DayKey, typeof defaultHours>>({
    sun: { ...defaultHours, enabled: false },
    mon: { ...defaultHours },
    tue: { ...defaultHours },
    wed: { ...defaultHours },
    thu: { ...defaultHours },
    fri: { ...defaultHours },
    sat: { ...defaultHours, enabled: false },
  });

  const [timezone, setTimezone] = useState<string>(timezones[0].value);

  // Load existing settings on mount
  useEffect(() => {
    if (me?.working_hours) {
      const incoming: any = me.working_hours;
      const transformed: Record<DayKey, any> = { ...hours };
      (Object.keys(incoming) as DayKey[]).forEach((k) => {
        transformed[k] = {
          start: incoming[k]?.start ?? "09:00",
          end: incoming[k]?.end ?? "17:00",
          enabled: !!incoming[k],
        };
      });
      setHours(transformed);
    }
    if (me?.timezone) setTimezone(me.timezone);
  }, [me]);

  const handleTimeChange = (day: DayKey, field: "start" | "end", value: string) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const toggleDayEnabled = (day: DayKey, enabled: boolean) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled },
    }));
  };

  const handleSave = () => {
    const payload: any = {
      timezone,
      after_hours_enabled: true,
      working_hours: Object.keys(hours).reduce((acc, key) => {
        const d = hours[key as DayKey];
        acc[key] = d.enabled ? { start: d.start, end: d.end } : null;
        return acc;
      }, {} as Record<string, any>),
    };

    if (me?.id) {
      dispatch(usersUpdateUserNotAdmin({ id: me.id, data: payload }))
        .unwrap()
        .then((updatedUser) => {
          dispatch(authSlice.actions.setMeData(updatedUser));
          onClose();
        });
    }
  };

  return (
    <AppBox column gap={6} fullWidth>
      <AppBox className="text-gray-500">
        <AppText variant="body">
          The hours you set here are your Open Hours. So the times you are Closed are your After Hours,
          and will receive the special After Hours message.
        </AppText>
      </AppBox>
      {/* Timezone selector */}
      <AppBox column gap={1}>
        <AppText variant="label">Timezone</AppText>
        <AppDropdown
          label={timezones.find((tz) => tz.value === timezone)?.label || "Timezone"}
          items={timezones}
          value={timezone}
          onChange={(v) => setTimezone(String(v))}
        />
      </AppBox>

      {/* Weekly hours table */}
      <AppBox column gap={4} className="mt-4">
        {(Object.keys(dayLabels) as DayKey[]).map((day) => {
          const d = hours[day];
          return (
            <AppBox key={day} align="center" gap={2} className="w-full items-center">
              {/* Fixed-width day label */}
              <AppText className="w-6 text-center font-semibold flex-shrink-0">
                {dayLabels[day]}
              </AppText>

              {d.enabled ? (
                <>
                  {/* Start time */}
                  <AppDropdown
                    label={timeOptions.find((t) => t.value === d.start)?.label || ""}
                    items={timeOptions}
                    value={d.start}
                    onChange={(val) => handleTimeChange(day, "start", String(val))}
                    className="w-28"
                  />
                  <AppText>-</AppText>
                  {/* End time */}
                  <AppDropdown
                    label={timeOptions.find((t) => t.value === d.end)?.label || ""}
                    items={timeOptions}
                    value={d.end}
                    onChange={(val) => handleTimeChange(day, "end", String(val))}
                    className="w-28"
                  />

                  {/* flexible spacer pushes icon right */}
                  <AppBox />

                  {/* Disable button */}
                  <AppButton
                    variant="text"
                    size="small"
                    onClick={() => toggleDayEnabled(day, false)}
                  >
                    <AppIcon name="x" size="sm" />
                  </AppButton>
                </>
              ) : (
                <AppBox justify="between" align="center" fullWidth>
                  <AppBox>
                    <AppText variant="body">Closed</AppText>
                  </AppBox>

                  <AppBox width={62}>
                    <AppButton
                      variant="text"
                      size="small"
                      onClick={() => toggleDayEnabled(day, true)}
                    >
                      <AppIcon name="plus" size="sm" />
                    </AppButton>
                  </AppBox>
                </AppBox>
              )}
            </AppBox>
          );
        })}
      </AppBox>

      {/* Action buttons */}
      <AppBox gap={4} className="justify-end mt-6">
        <AppButton variant="outlined" onClick={onClose}>Cancel</AppButton>
        <AppButton onClick={handleSave}>Save Changes</AppButton>
      </AppBox>
    </AppBox>
  );
};

export default AfterHoursModal;